import { api } from "./api.ts";

export type ClientIntakeRequest = {
  name: string;
  email: string;
  password: string;
  password_confirm: string;
  phone_number: string;
  organization_slug: string;
};

export type ClientIntakeResponse = {
  success: boolean;
  message?: string;
  client_id?: number;
  user_id?: number;
};

export type LoginIntakeRequest = {
  email: string;
  password: string;
};

export type LoginIntakeResponse = {
  message: string;
  user_id: number;
  email: string;
  username: string;
  access_token: string;
  refresh_token: string;
  liberty_referral_fields_enabled: boolean;
};

export type PresignedUploadUrlRequest = {
  filename: string;
  intake_identifier: string;
  content_type?: string;
  extra_tags?: Record<string, string>;
};

export type IntakeSubmissionRequest = {
  organization_slug: string;
  intake_identifier: string;
  documents: Array<{
    filename: string;
    s3_key: string;
    type: string;
    is_cms_submission_document: boolean;
  }>;
  document_count: number;
  adjuster: {
    name: string;
    email: string;
    street: string;
    apt?: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    adjuster_same_as_referring_party?: boolean;
  };
  claimant: {
    first_name: string;
    middle_initial?: string;
    last_name: string;
    gender: string;
    ssn: string;
    birth_date: string;
    address: string;
    city: string;
    state: string;
    zip_code: string;
    medicare_id?: string;
    is_medicare_eligible: boolean;
    reasonable_expectation: boolean;
  };
  employer: {
    name: string;
    address: string;
    state: string;
    zipcode: string;
  };
  insurer: {
    name: string;
    address: string;
    state: string;
    zipcode: string;
  };
  tpa: {
    name: string;
    address: string;
    state: string;
    zipcode: string;
  };
  dates_of_injury: {
    date: string;
    claim_numbers: { number: string }[];
    jurisdiction: string;
    wcmsa_zip_code: string;
    one_eleven_jurisdiction: string;
    accident_description: string;
    description_of_claimants_job: string;
    accepted_conditions: string;
    denied_conditions: string;
  }[];
  is_rush: boolean;
  is_24_hour_rush: boolean;
  is_3_day_rush: boolean;
  is_update_to_prior_msa?: boolean;
  due_date?: string;
  is_represented?: boolean;
  claimant_counsel?: string;
  attorney_name?: string;
  attorney_address?: string;
  attorney_phone?: string;
  services?: Array<{ type: string }>;
  insurance_types?: Array<{ type: string }>;
  settlement_reached?: boolean;
  estimated_settlement_amount?: string;
  estimated_medical_settlement_value?: string;
  reserve_settlement_amount?: string;
  tentative_settlement_minimum?: string;
  tentative_settlement_maximum?: string;
  preferred_structure_settlement_broker?: string;
  referring_party_name?: string;
  // Extra addresses to CC on the referral acknowledgement email, in
  // addition to the referring party. Applies to every intake form.
  ack_email_recipients?: { email: string }[];
  awarded_ssd?: boolean;
  applied_ssd?: boolean;
  // Liberty Mutual referral fields -- only meaningful for Liberty
  // Mutual/Helmsman submitters; the backend accepts/ignores these based on
  // the submitting user, not on the presence of these keys.
  grs_or_legacy_rcg?: string;
  segmentation_type?: string;
  rcg_carrier?: string;
  referral_type?: string;
  wcrc_case_id?: string;
  claim_notes?: string;
};

export type IntakeSubmissionResponse = {
  success: boolean;
  message?: string;
};

export type InitiatePasswordResetRequest = {
  email: string;
};

export type InitiatePasswordResetResponse = {
  message: string;
};

export type ConfirmPasswordResetRequest = {
  password_reset_token: string;
  password1: string;
  password2: string;
};

export type ConfirmPasswordResetResponse = {
  message: string;
};

export const intakeAPI = api.injectEndpoints({
  endpoints: (build) => ({
    registerClientIntake: build.mutation<
      ClientIntakeResponse,
      ClientIntakeRequest
    >({
      query: (data) => ({
        url: `api/v1/client-intake-registration/`,
        method: "POST",
        body: data,
      }),
    }),
    loginIntake: build.mutation<LoginIntakeResponse, LoginIntakeRequest>({
      query: (data) => ({
        url: `api/v1/login`,
        method: "POST",
        body: data,
      }),
    }),
    createSubmission: build.mutation({
      query: ({ accessToken, intakeToken }) => ({
        url: `api/v1/client-intake-submission/`,
        method: "POST",
        body: {},
        headers: {
          Authorization: intakeToken
            ? `Intake-Bearer ${intakeToken}`
            : `Bearer ${accessToken}`,
        },
      }),
    }),
    submitIntake: build.mutation<
      IntakeSubmissionResponse,
      {
        data: IntakeSubmissionRequest;
        accessToken: string;
        intakeToken?: string;
      }
    >({
      query: ({ data, intakeToken, accessToken }) => ({
        url: `api/v1/client-intake`,
        method: "POST",
        body: data,
        headers: {
          Authorization: intakeToken
            ? `Intake-Bearer ${intakeToken}`
            : `Bearer ${accessToken}`,
        },
      }),
    }),
    initiatePasswordReset: build.mutation<
      InitiatePasswordResetResponse,
      InitiatePasswordResetRequest
    >({
      query: (data) => ({
        url: `api/v1/password-reset/initiate/`, // Fixed: removed 'intake' prefix
        method: "POST",
        body: data,
      }),
    }),
    confirmPasswordReset: build.mutation<
      ConfirmPasswordResetResponse,
      ConfirmPasswordResetRequest
    >({
      query: (data) => ({
        url: `api/v1/password-reset/confirm/`, // Fixed: removed 'intake' prefix
        method: "POST",
        body: data,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useRegisterClientIntakeMutation,
  useLoginIntakeMutation,
  useSubmitIntakeMutation,
  useCreateSubmissionMutation,
  useInitiatePasswordResetMutation,
  useConfirmPasswordResetMutation,
} = intakeAPI;
